import { DisplayRow, GanttTask } from '../../types/gantt.types'
import { Badge, Checkbox, IconWarningCircle, Input } from '@indipia/ui-core'
import { badgeAriaLabel, hasUnsent, pickBadgeTarget, showBadge, sumCounts } from '@indipia/shared-features'
// 좌측 고정 열 한 행(체크 · 이름 · 변경 배지 · 메모 · 소요일/구간)

export type GanttTaskRowLeftProps = {
  rowBg: "color-mix(in srgb, var(--gt-primary) 10%, var(--shell-surface))" | "var(--shell-bg)" | "color-mix(in srgb, var(--gt-subtle) 45%, var(--shell-surface))" | "var(--shell-surface)"
  rowH: number
  rowLineColor: "color-mix(in srgb, var(--gt-primary) 25%, var(--shell-surface))" | "var(--shell-border)"
  row: DisplayRow
  allSelected: boolean
  setSelectedTaskIds: React.Dispatch<React.SetStateAction<Set<number>>>
  primary: GanttTask
  localRowNames: Record<number, string>
  setLocalRowNames: React.Dispatch<React.SetStateAction<Record<number, string>>>
  handleRowNameBlur: (taskId: number) => Promise<void>
  rowIsPending: boolean
  visibleMemoId: number | null
  setVisibleMemoId: React.Dispatch<React.SetStateAction<number | null>>
  setMemoTooltipPos: React.Dispatch<React.SetStateAction<{ x: number; y: number; } | null>>
  handleRowDurationChange: (taskIds: number[], delta: number) => void
  setExpandedRows: React.Dispatch<React.SetStateAction<Set<string>>>
  /** 소요일(작업일 수) — 공휴일 정보를 쓸 수 없으면 null('–' 표시) */
  durationOf: (t: GanttTask) => number | null
  /** 고객 요청 배지 클릭 — 해당 공정 상세창을 열고 고객 요청 카드로 이동 */
  onOpenCustReq?: (taskId: number) => void
}

export function GanttTaskRowLeft({
  rowBg, rowH, rowLineColor, row, allSelected, setSelectedTaskIds, primary, localRowNames, setLocalRowNames,
  handleRowNameBlur, rowIsPending, visibleMemoId, setVisibleMemoId, setMemoTooltipPos,
  handleRowDurationChange, setExpandedRows, durationOf, onOpenCustReq,
}: GanttTaskRowLeftProps) {
  // 고객 요청 배지 — 접힌 구간 행은 구간 합계, 펼친 구간 행·단일 행은 각자 값. 집계 실패(null)·0건이면 그리지 않는다.
  const cr = sumCounts(row.bars)
  const crTarget = pickBadgeTarget(row.bars)
  const crUnsent = hasUnsent(cr.unsentCount)
  const openCustReq = () => { if (crTarget !== null) onOpenCustReq?.(crTarget) }
  return (
    <>
      <div style={{ width: 212, flexShrink: 0, display: 'flex', position: 'sticky', left: 0, zIndex: 3, background: rowBg, height: rowH, minHeight: rowH, boxSizing: 'border-box', boxShadow: `inset 0 -1px 0 ${rowLineColor}` }}>
      {/* Name cell — 펼쳐진 개별 구간 행(isExpandedChild)은 들여쓰기 + 연결선 아이콘으로
          "이 공종 아래에 나열된 하위 구간"임을 표시한다. */}
      <div style={{ width: 160, padding: row.isExpandedChild ? '4px 12px 4px 22px' : '4px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, borderRight: `var(--border-width-hairline) solid ${rowLineColor}`, height: rowH, minHeight: rowH, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative', overflow: 'hidden' }}>
          {row.isExpandedChild && (
            <span style={{ position: 'absolute', left: -16, fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', flexShrink: 0 }}>↳</span>
          )}
          <Checkbox
            aria-label={`${row.workTypeName} 선택`}
            checked={allSelected}
            onMouseDown={e => e.stopPropagation()}
            onChange={e => {
              setSelectedTaskIds(prev => {
                const next = new Set(prev)
                for (const b of row.bars) { if (e.target.checked) next.add(b.taskId); else next.delete(b.taskId) }
                return next
              })
            }}
            className="shrink-0"
            style={{ accentColor: 'var(--color-error)' }}
          />
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: primary.color, flexShrink: 0 }} />
          {/* 이름 입력칸 — 체크박스·색점·변경 배지·메모 아이콘 등 나머지 요소가 항상
              보이도록, 고정폭 대신 남는 공간만 차지하고(flex:1, minWidth:0) 넘치는
              글자만 말줄임표로 자른다(옆 "N구간" 버튼 위로 겹쳐 가려지는 문제 방지). */}
          <Input
            value={localRowNames[primary.taskId] ?? row.workTypeName}
            onMouseDown={e => e.stopPropagation()}
            onChange={e => setLocalRowNames(prev => ({ ...prev, [primary.taskId]: e.target.value }))}
            onBlur={e => { e.currentTarget.style.background = 'transparent'; handleRowNameBlur(primary.taskId) }}
            onFocus={e => (e.currentTarget.style.background = 'var(--shell-bg)')}
            aria-label="공종명"
            title={`${localRowNames[primary.taskId] ?? row.workTypeName} — 공종명을 사장님이 주로 쓰는 이름으로 바꿀 수 있습니다 — 비우면 표준 공종명으로 돌아갑니다`}
            width="fill"
            className="flex-1 min-w-0"
            style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', border: 'none', background: 'transparent', padding: '1px 2px', borderRadius: 4 }}
          />
          {rowIsPending && <span style={{ fontSize: 'var(--type-caption-size)', background: 'var(--gt-primary)', color: 'var(--shell-surface)', borderRadius: 4, padding: '1px 5px', fontWeight: 700, flexShrink: 0 }}>변경</span>}
          {showBadge(cr.requestCount) && onOpenCustReq && (
            <Badge
              variant="soft"
              status={crUnsent ? 'warning' : 'info'}
              role="button"
              tabIndex={0}
              title={badgeAriaLabel(cr.requestCount as number, cr.unsentCount)}
              aria-label={badgeAriaLabel(cr.requestCount as number, cr.unsentCount)}
              className="shrink-0 cursor-pointer"
              // 이름 칸(160px)이 좁아 기본 배지(높이 24 · 좌우 10px · '요청 N')를 쓰면 공종명이 '설..'로 잘린다 —
              // 숫자만 담은 작은 알약으로 줄이고, '요청 N건, 미전달 N건'은 title · aria-label 로 알린다
              style={{ height: 'var(--badge-height-sm)', minWidth: 'var(--badge-height-sm)', padding: '0 var(--spacing-02)', gap: 2, justifyContent: 'center', borderRadius: 'var(--radius-full)', fontSize: 'var(--type-caption-size)' }}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); openCustReq() }}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openCustReq() } }}
            >
              {crUnsent && <IconWarningCircle width={10} height={10} />}
              {cr.requestCount as number}
            </Badge>
          )}
          {primary.description && (
            <div data-memo-popup style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
              <button
                onMouseDown={e => e.stopPropagation()}
                onClick={e => {
                  e.stopPropagation()
                  if (visibleMemoId === primary.taskId) {
                    setVisibleMemoId(null); setMemoTooltipPos(null)
                  } else {
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                    setMemoTooltipPos({ x: rect.left, y: rect.bottom + 4 })
                    setVisibleMemoId(primary.taskId)
                  }
                }}
                aria-label="메모 보기"
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '1px 2px', display: 'flex', alignItems: 'center', color: visibleMemoId === primary.taskId ? 'var(--gt-primary)' : 'var(--shell-hint)', lineHeight: 1 }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Duration cell — 바(구간)가 1개면 숫자+스피너, 2개 이상이면 "N구간" 펼치기/
          접기 버튼으로 바뀐다(합계·단일값으로는 어느 구간을 바꾸라는 건지 특정할 수
          없으므로, 버튼을 눌러 구간마다 독립된 행으로 펼쳐서 따로 수정하게 한다). */}
      <div style={{ width: 52, flexShrink: 0, borderRight: `var(--border-width-hairline) solid ${rowLineColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, height: rowH, boxSizing: 'border-box' }}>
        {row.bars.length === 1 && !row.isGroupHeader ? (
          <>
            <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface)', minWidth: 16, textAlign: 'center' }}>{durationOf(primary) ?? '–'}</span>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
              <button onMouseDown={e => e.stopPropagation()} onClick={() => handleRowDurationChange(row.bars.map(b => b.taskId), 1)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 2px', fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', lineHeight: 1 }} aria-label="소요일 증가">▲</button>
              <button onMouseDown={e => e.stopPropagation()} onClick={() => handleRowDurationChange(row.bars.map(b => b.taskId), -1)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 2px', fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', lineHeight: 1 }} aria-label="소요일 감소">▼</button>
            </div>
          </>
        ) : (
          <button
            onMouseDown={e => e.stopPropagation()}
            onClick={e => {
              e.stopPropagation()
              setExpandedRows(prev => {
                const next = new Set(prev)
                if (next.has(row.workTypeCode)) next.delete(row.workTypeCode); else next.add(row.workTypeCode)
                return next
              })
            }}
            style={{ border: 'none', borderRadius: 24, background: row.isGroupHeader ? 'var(--gt-primary)' : 'var(--gt-subtle)', color: row.isGroupHeader ? 'var(--shell-surface)' : 'var(--gt-primary)', cursor: 'pointer', fontSize: 'var(--type-caption-size)', fontWeight: 700, padding: '2px 7px', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
          >
            {row.bars.length}구간 {row.isGroupHeader ? '▲' : '▼'}
          </button>
        )}
      </div>
      </div>{/* end sticky left */}
    </>
  )
}
